import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { OptionSelect } from "@/components/FormBits";
import { PaymentBadge, BookingStatusBadge } from "@/components/StatusBadges";
import { useBookingDialog } from "@/context/BookingDialogContext";
import { useFacilities } from "@/hooks/useFacilities";
import { api, formatApiError } from "@/lib/api";
import { PAYMENT_STATUS, BOOKING_STATUS, eur, fmtDate, facilityColor } from "@/lib/constants";

const labels = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, v.label]));

const PaymentQuickSelect = ({ b, onDone }) => {
  const change = async (v) => {
    try {
      await api.patch(`/bookings/${b.id}/payment`, { payment_status: v });
      toast.success(`Pagamento: ${PAYMENT_STATUS[v].label}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Select value={b.payment_status} onValueChange={change}>
      <SelectTrigger data-testid={`payment-status-toggle-${b.id}`} className="h-auto w-auto border-0 bg-transparent p-0 shadow-none focus:ring-0 gap-1">
        <PaymentBadge status={b.payment_status} />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(PAYMENT_STATUS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`payment-option-${b.id}-${k}`}>{v.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

const Totals = ({ rows }) => {
  const active = rows.filter((b) => b.booking_status !== "annullata");
  const sum = (s) => active.filter((b) => !s || b.payment_status === s).reduce((t, b) => t + b.amount, 0);
  return (
    <div className="grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200" data-testid="bookings-totals">
      {[["Totale importi", sum(), "total"], ["Incassato", sum("pagato"), "paid"], ["Da incassare", sum("da_pagare"), "due"]].map(([l, v, id]) => (
        <div key={id} className="bg-white px-5 py-4"><p className="eyebrow">{l}</p><p data-testid={`totals-${id}`} className="mt-1 font-mono text-xl">{eur(v)}</p></div>
      ))}
    </div>
  );
};

export default function Bookings() {
  const { openEdit, version, refresh } = useBookingDialog();
  const [facilities] = useFacilities();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ q: "", facility: "all", payment_status: "all", booking_status: "all" });

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "all"));
    const t = setTimeout(() => api.get("/bookings", { params }).then((r) => setRows(r.data)), 250);
    return () => clearTimeout(t);
  }, [f, version]);

  const names = useMemo(() => Object.fromEntries(facilities.map((x) => [x.slug, x.name])), [facilities]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="bookings-page" className="space-y-6">
      <div>
        <p className="eyebrow">Registro pratiche</p>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-semibold text-slate-900">Prenotazioni e pagamenti</h1>
      </div>

      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="bookings-search-input" value={f.q} onChange={(e) => set("q")(e.target.value)} placeholder="Cerca per evento, richiedente, riferimento…" className="pl-9 bg-white" />
        </div>
        <OptionSelect testId="booking-facility-filter" className="lg:w-60" value={f.facility} onChange={set("facility")} options={{ all: "Tutte le strutture", ...names }} />
        <OptionSelect testId="booking-status-filter" className="lg:w-44" value={f.payment_status} onChange={set("payment_status")} options={{ all: "Ogni pagamento", ...labels(PAYMENT_STATUS) }} />
        <OptionSelect testId="booking-state-filter" className="lg:w-44" value={f.booking_status} onChange={set("booking_status")} options={{ all: "Ogni stato", ...labels(BOOKING_STATUS) }} />
      </div>

      <Totals rows={rows} />

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <Table data-testid="bookings-table">
          <TableHeader>
            <TableRow className="bg-[#FBFAF7]">
              {["Data", "Struttura", "Evento", "Richiedente", "Importo", "Pagamento", "Stato", ""].map((h) => <TableHead key={h} className="eyebrow">{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-12 text-center text-slate-500" data-testid="bookings-empty">Nessuna prenotazione trovata.</TableCell></TableRow>
            )}
            {rows.map((b) => (
              <TableRow key={b.id} data-testid={`booking-table-row-${b.id}`} className={b.booking_status === "annullata" ? "opacity-60" : ""}>
                <TableCell className="whitespace-nowrap"><div className="font-semibold">{fmtDate(b.date)}</div><div className="font-mono text-xs text-slate-500">{b.start_time}–{b.end_time}</div></TableCell>
                <TableCell><span className="flex items-center gap-2 whitespace-nowrap"><span className={`h-2.5 w-2.5 rounded-sm ${facilityColor(b.facility_slug).dot}`} />{names[b.facility_slug]}</span></TableCell>
                <TableCell><div className="font-semibold text-slate-900">{b.title}</div><div className="text-xs text-slate-500">{b.event_type}</div></TableCell>
                <TableCell><div>{b.applicant_name}</div><div className="text-xs text-slate-500">{b.applicant_contact}</div></TableCell>
                <TableCell className="font-mono whitespace-nowrap">{eur(b.amount)}</TableCell>
                <TableCell><PaymentQuickSelect b={b} onDone={refresh} />{b.payment_reference && <div className="mt-1 font-mono text-[11px] text-slate-400">{b.payment_reference}</div>}</TableCell>
                <TableCell><BookingStatusBadge status={b.booking_status} /></TableCell>
                <TableCell><Button variant="ghost" size="icon" onClick={() => openEdit(b)} data-testid={`booking-edit-${b.id}`}><Pencil className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
